import type { GoogleCredential } from '@zuruflex/api-client';
import type { Partner, PartnerSession } from '@zuruflex/types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { session as zuruflexSession } from './api.js';

interface AuthState {
  user: PartnerSession['user'] | null;
  partner: Partner | null;
  isRestoring: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (
    credential: GoogleCredential,
    signUp?: Record<string, unknown>,
  ) => Promise<void>;
  signUp: (input: Record<string, unknown>) => Promise<void>;
  signOut: () => Promise<void>;
  /** After editing the business details, so the shell reflects them at once. */
  refreshPartner: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<PartnerSession | null>(
    () => zuruflexSession.current as PartnerSession | null,
  );
  const [isRestoring, setIsRestoring] = useState(zuruflexSession.canRestore);

  useEffect(() => {
    // A background refresh that fails signs the partner out from inside
    // some unrelated request; without this the shell would not notice.
    return zuruflexSession.subscribe((next) => setCurrent(next as PartnerSession | null));
  }, []);

  useEffect(() => {
    if (!zuruflexSession.canRestore) return;
    let cancelled = false;
    void zuruflexSession.restore().finally(() => {
      if (!cancelled) setIsRestoring(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await zuruflexSession.signInWithPassword(email, password);
  }, []);

  const signInWithGoogle = useCallback(
    async (credential: GoogleCredential, signUp?: Record<string, unknown>) => {
      await zuruflexSession.signInWithGoogle(credential, signUp);
    },
    [],
  );

  const signUp = useCallback(async (input: Record<string, unknown>) => {
    await zuruflexSession.signUp(input);
  }, []);

  const signOut = useCallback(async () => {
    await zuruflexSession.signOut();
  }, []);

  const refreshPartner = useCallback(async () => {
    const existing = zuruflexSession.current as PartnerSession | null;
    if (!existing) return;
    const partner = await zuruflexSession.client.myPartner();
    setCurrent({ ...existing, partner });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user: current?.user ?? null,
      partner: current?.partner ?? null,
      isRestoring,
      signIn,
      signInWithGoogle,
      signUp,
      signOut,
      refreshPartner,
    }),
    [current, isRestoring, signIn, signInWithGoogle, signUp, signOut, refreshPartner],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/**
 * Inside the portal shell there is always a partner, and every page would
 * otherwise begin by narrowing away a null the router has already ruled out.
 */
export function usePartner(): { user: PartnerSession['user']; partner: Partner } {
  const { user, partner } = useAuth();
  if (!user || !partner) throw new Error('usePartner used outside a signed-in route');
  return { user, partner };
}
